import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SuiteButton } from "@/components/suite";
import { ArrowLeft, Save, Palette, Copy, Link2 } from "lucide-react";
import CompanyAccessSection from "@/components/access/CompanyAccessSection";
import CompanyContractsPanel from "@/components/settings/CompanyContractsPanel";
import { useEffectiveRole } from "@/foundation/context";
import { TimesheetCompanySettings } from "@/components/timesheet/TimesheetCompanySettings";
import { DurationFormatSelect, DurationInput, DurationLabel, announceCompanyDurationFormat } from "@/foundation/duration";
import { AnagraficaFiscaleSection, useAnagraficaValidation } from "@/foundation/anagrafica";

const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const VALID_TABS = ["anagrafica","contratti","banca-ore","accessi","portale"];

// Convertitori Santo Patrono (GG-MM ↔ MM-DD) — riusiamo la logica esistente di Companies.jsx.
function italianToMmdd(v) {
  const s = String(v || "").trim();
  if (!s) return { value: "", error: null };
  const m = s.match(/^(\d{1,2})[-/](\d{1,2})$/);
  if (!m) return { value: "", error: "Formato non valido (GG-MM)" };
  const dd = parseInt(m[1], 10), mm = parseInt(m[2], 10);
  if (isNaN(dd) || isNaN(mm) || mm < 1 || mm > 12 || dd < 1 || dd > 31) return { value: "", error: "Data non valida" };
  return { value: `${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`, error: null };
}
function mmddToItalian(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{2})-(\d{2})$/); if (!m) return "";
  return `${m[2]}-${m[1]}`;
}
function toSlug(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

export default function CompanyEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const isExternalConsultant = (effectiveRole === "external_consultant");

  const currentTab = useMemo(() => {
    const t = searchParams.get("tab");
    return VALID_TABS.includes(t) ? t : "anagrafica";
  }, [searchParams]);

  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [patronoDisplay, setPatronoDisplay] = useState("");
  const [patronoError, setPatronoError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialPatronoRef = useRef("");
  const validation = useAnagraficaValidation(form);

  const loadCompany = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/companies/${id}`);
      const data = r.data;
      const filled = {
        name: data.name || "",
        tax_code: data.tax_code || "",
        vat: data.vat || "",
        email: data.email || "",
        phone: data.phone || "",
        pec: data.pec || "",
        rea_number: data.rea_number || "",
        address: data.address || "",
        cap: data.cap || "",
        comune: data.comune || "",
        provincia: data.provincia || "",
        notes: data.notes || "",
        notify_external_consultant: !!data.notify_external_consultant,
        bank_hours_enabled: data.bank_hours_enabled !== false,
        compensation_period_months: data.compensation_period_months || 6,
        semester_start_month: data.semester_start_month || 1,
        active_month: data.active_month || 1,
        active_year: data.active_year || new Date().getFullYear(),
        santo_patrono_name: data.santo_patrono_name || "",
        santo_patrono_date: data.santo_patrono_date || "",
        consultant_report_mode: data.consultant_report_mode || "consuntivo_effettivo",
        attendance_autofill_mode: data.attendance_autofill_mode || "ask",
        duration_format: data.duration_format || "decimal",
        timesheet_prefill_mode: data.timesheet_prefill_mode || "empty",
        overtime_allocation: data.overtime_allocation || null,
        timesheet_next_mode: data.timesheet_next_mode || "ask",
        logo_url: data.logo_url || "",
        primary_color: data.primary_color || "",
        portal_slug: data.portal_slug || "",
        custom_settings: { ...(data.custom_settings || {}) },
      };
      setCompany(data);
      setForm(filled);
      setInitialForm(filled);
      const pd = mmddToItalian(filled.santo_patrono_date);
      setPatronoDisplay(pd);
      initialPatronoRef.current = pd;
    } catch (e) {
      toast.error(apiErr(e) || "Azienda non trovata");
      navigate("/aziende", { replace: true });
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { if (id) loadCompany(); }, [id, loadCompany]);

  const isDirty = useMemo(() => {
    if (!form || !initialForm) return false;
    return JSON.stringify(form) !== JSON.stringify(initialForm)
        || patronoDisplay !== initialPatronoRef.current;
  }, [form, initialForm, patronoDisplay]);

  // Avviso beforeunload (browser tab close/reload)
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Blocco navigazione React Router — non disponibile con BrowserRouter classico,
  // sostituito dal `window.confirm` nei click di uscita (goBack) e nei cambi scheda.

  const changeTab = (t) => {
    // Il cambio scheda NON blocca: le modifiche non salvate restano in memoria
    // e vengono avvisate solo all'uscita reale della pagina (goBack / beforeunload).
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: false });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome azienda obbligatorio"); return; }
    if (!form.pec || !form.pec.trim()) {
      toast.error("PEC obbligatoria");
      setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", "anagrafica"); return n; }, { replace: true });
      return;
    }
    if (validation.firstError) {
      validation.revealAll();
      toast.error(validation.firstError);
      setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", "anagrafica"); return n; }, { replace: true });
      return;
    }
    if (patronoDisplay && patronoError) { toast.error(patronoError); return; }
    const payload = { ...form };
    payload.pec = (payload.pec || "").trim();
    payload.rea_number = (payload.rea_number || "").trim();
    if (!payload.portal_slug) payload.portal_slug = toSlug(payload.name);
    if (patronoDisplay) {
      const conv = italianToMmdd(patronoDisplay);
      if (conv.error) { toast.error(conv.error); return; }
      payload.santo_patrono_date = conv.value || null;
    } else { payload.santo_patrono_date = null; }
    if (payload.custom_settings) {
      // v0.99.85+: pec/rea_number ora sono colonne anagrafica, non custom_settings.
      const cleaned = { ...payload.custom_settings };
      delete cleaned.pec;
      delete cleaned.rea_number;
      const cs = Object.fromEntries(Object.entries(cleaned).filter(([_, v]) => v !== "" && v !== null && v !== undefined));
      payload.custom_settings = cs;
    }
    setSaving(true);
    try {
      await api.patch(`/companies/${id}`, payload);
      announceCompanyDurationFormat(id, payload.duration_format);
      toast.success("Azienda aggiornata");
      setInitialForm(form);
      initialPatronoRef.current = patronoDisplay;
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const goBack = () => {
    if (isDirty) {
      const ok = window.confirm("Ci sono modifiche non salvate. Uscire senza salvare?");
      if (!ok) return;
    }
    navigate("/aziende");
  };

  if (loading || !form) {
    return <div className="p-6 text-sm text-slate-500">Caricamento azienda…</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-4">
        <button
          type="button"
          onClick={goBack}
          className="text-sm text-suite-primary hover:underline inline-flex items-center gap-1 mb-2"
          data-testid="company-back-btn"
        >
          <ArrowLeft size={14} /> Torna alle aziende
        </button>
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="company-title">Modifica Azienda</h1>
        <p className="text-sm text-slate-500">{company?.name} — Dati e configurazione aziendale</p>
      </div>

      <Tabs value={currentTab} onValueChange={changeTab} className="w-full">
        {/* Barra schede sticky */}
        <div className="sticky top-0 z-10 bg-slate-50 -mx-4 md:-mx-6 px-4 md:px-6 py-2 border-b border-slate-200">
          <TabsList className="w-full overflow-x-auto flex md:grid md:grid-cols-5 gap-1 justify-start">
            <TabsTrigger value="anagrafica" data-testid="company-tab-anagrafica" className="whitespace-nowrap">Anagrafica</TabsTrigger>
            <TabsTrigger value="contratti"  data-testid="company-tab-contratti"  className="whitespace-nowrap">Contratti e istituti</TabsTrigger>
            <TabsTrigger value="banca-ore"  data-testid="company-tab-banca-ore"  className="whitespace-nowrap">Banca ore e calendario</TabsTrigger>
            <TabsTrigger value="accessi"    data-testid="company-tab-accessi"    className="whitespace-nowrap" disabled={isExternalConsultant}>Accessi aziendali</TabsTrigger>
            <TabsTrigger value="portale"    data-testid="company-tab-portale"    className="whitespace-nowrap">Personalizzazione portale</TabsTrigger>
          </TabsList>
        </div>

        {/* Anagrafica */}
        <TabsContent value="anagrafica" className="mt-6">
          <fieldset disabled={isExternalConsultant} className="grid md:grid-cols-2 gap-4 disabled:opacity-100">
            <div className="md:col-span-2">
              <AnagraficaFiscaleSection form={form} setForm={setForm} validation={validation}
                readOnly={isExternalConsultant} pecRequired tid="company-form" />
            </div>
            <div className="md:col-span-2 rounded-lg border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.notify_external_consultant}
                  onChange={(e) => setForm({ ...form, notify_external_consultant: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-emerald-700"
                  data-testid="company-notify-external-consultant" />
                <span>
                  <span className="block text-sm font-semibold text-suite-neutral-800">Notifica il consulente esterno</span>
                  <span className="block text-xs text-suite-neutral-500 mt-0.5">Invia una sola notifica quando il cartellino mensile viene chiuso definitivamente.</span>
                </span>
              </label>
            </div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </fieldset>
        </TabsContent>

        {/* Contratti e istituti — riutilizza componente esistente */}
        <TabsContent value="contratti" className="mt-6">
          <CompanyContractsPanel companyId={id} readOnly={isExternalConsultant} />
        </TabsContent>

        {/* Banca ore e calendario */}
        <TabsContent value="banca-ore" className="mt-6">
          <fieldset disabled={isExternalConsultant} className="grid md:grid-cols-2 gap-4 disabled:opacity-100">
            <div>
              <Label>Gestione banca ore</Label>
              <Select value={form.bank_hours_enabled ? "on" : "off"} onValueChange={(v) => setForm({ ...form, bank_hours_enabled: v === "on" })}>
                <SelectTrigger data-testid="company-form-bank-hours"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="on">Attiva</SelectItem><SelectItem value="off">Nessuna</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Periodo compensazione (mesi)</Label>
              <Select value={String(form.compensation_period_months)}
                      onValueChange={(v) => setForm({ ...form, compensation_period_months: Number(v) })}
                      disabled={!form.bank_hours_enabled}>
                <SelectTrigger data-testid="company-form-compensation-period"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="3">3</SelectItem><SelectItem value="6">6</SelectItem><SelectItem value="12">12</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Inizio semestre</Label>
              <Select value={String(form.semester_start_month)}
                      onValueChange={(v) => setForm({ ...form, semester_start_month: Number(v) })}
                      disabled={!form.bank_hours_enabled}>
                <SelectTrigger data-testid="company-form-semester-start"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mese attivo</Label>
              <Select value={String(form.active_month)} onValueChange={(v) => setForm({ ...form, active_month: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Anno attivo</Label><Input type="number" value={form.active_year} onChange={(e) => setForm({ ...form, active_year: Number(e.target.value) })} /></div>
            <div>
              <Label>Data Santo Patrono (GG-MM)</Label>
              <Input placeholder="es. 20-01" value={patronoDisplay} maxLength={5}
                onChange={(e) => { setPatronoDisplay(e.target.value); setPatronoError(italianToMmdd(e.target.value).error); }}
                data-testid="company-form-patrono-date" />
              {patronoError && <div className="text-xs text-red-600 mt-1">{patronoError}</div>}
            </div>
            <div>
              <Label>Nome Santo Patrono</Label>
              <Input value={form.santo_patrono_name} onChange={(e) => setForm({ ...form, santo_patrono_name: e.target.value })} placeholder="es. S. Vitale Martire" data-testid="company-form-patrono-name" />
            </div>
            <div>
              <DurationLabel data-testid="company-form-anomaly-threshold">Soglia anomalia saldo (h)</DurationLabel>
              <DurationInput allowNegative value={form.custom_settings?.anomaly_threshold_hours ?? ""}
                onCommit={(n) => setForm((f) => ({ ...f, custom_settings: { ...f.custom_settings, anomaly_threshold_hours: n ?? "" } }))}
                placeholder="es. -20"
                data-testid="company-form-anomaly-threshold" />
            </div>
            <div className="md:col-span-2">
              <Label>Modalità foglio presenze per il consulente</Label>
              <Select value={form.consultant_report_mode || "consuntivo_effettivo"}
                      onValueChange={(v) => setForm({ ...form, consultant_report_mode: v })}>
                <SelectTrigger data-testid="company-form-consultant-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="consuntivo_effettivo">Consuntivo effettivo</SelectItem>
                  <SelectItem value="orario_teorico_eccezioni">Orario teorico + eccezioni</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Determina come viene generato ogni mese il <b>Foglio presenze PDF (consulente)</b>.
                <br />
                <b>Consuntivo effettivo</b>: stampa le ore effettivamente lavorate come ordinarie.
                <br />
                <b>Orario teorico + eccezioni</b>: stampa l&apos;orario teorico contrattuale come ordinarie
                e riporta separatamente ferie, permessi, malattia, congedi, festività, straordinari,
                lavoro festivo e altre eccezioni.
                <br />
                Non modifica presenze, banca ore, saldi o altri report.
              </p>
            </div>
            <div className="md:col-span-2">
              <DurationFormatSelect value={form.duration_format} disabled={isExternalConsultant}
                onChange={(v) => setForm({ ...form, duration_format: v })} />
              <TimesheetCompanySettings form={form} setForm={setForm} disabled={isExternalConsultant} />
            </div>
            {/* v1.5.1 · Sezione Cartellino */}
            <div className="md:col-span-2 pt-2 mt-2 border-t">
              <Label className="text-slate-800 font-semibold">Compilazione automatica giornate (Cartellino)</Label>
              <Select value={form.attendance_autofill_mode || "ask"}
                      onValueChange={(v) => setForm({ ...form, attendance_autofill_mode: v })}>
                <SelectTrigger data-testid="company-form-attendance-autofill-mode" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Mai</SelectItem>
                  <SelectItem value="always_theoretical">Sempre dal teorico</SelectItem>
                  <SelectItem value="always_profile">Dal profilo orario</SelectItem>
                  <SelectItem value="ask">Chiedi ogni volta (consigliato)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Questa impostazione e' un <b>DEFAULT SUGGERITO</b>: nel Cartellino,
                aprendo <b>Strumenti</b>, l'operazione corrispondente sara' marcata come
                "Suggerito" e preselezionabile. Il consulente puo' comunque scegliere
                qualsiasi strumento del catalogo, indipendentemente da questa scelta.
                <br />
                Il pulsante <b>Salva</b> del Cartellino NON compila mai automaticamente:
                salva soltanto cio' che l'utente ha digitato.
                <br />
                <b>Mai</b>: nessuna elaborazione suggerita.
                <br />
                <b>Sempre dal teorico</b>: "Compila dal teorico" viene marcato "Suggerito".
                <br />
                <b>Dal profilo orario</b>: "Compila dal profilo orario" viene marcato "Suggerito".
                <br />
                <b>Chiedi ogni volta</b>: <span className="italic">default per le nuove aziende</span> — nessuna preselezione forte.
              </p>
            </div>
          </fieldset>
        </TabsContent>

        {/* Accessi aziendali */}
        <TabsContent value="accessi" className="mt-6">
          {isExternalConsultant ? (
            <div className="text-sm text-slate-500">Non disponibile per il ruolo consulente esterno.</div>
          ) : (
            <CompanyAccessSection companyId={id} currentUser={user} />
          )}
        </TabsContent>

        {/* Personalizzazione portale */}
        <TabsContent value="portale" className="mt-6">
          <fieldset disabled={isExternalConsultant} className="grid md:grid-cols-2 gap-4 disabled:opacity-100">
            <div className="md:col-span-2 border border-slate-200 rounded p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2"><Palette size={16} className="text-emerald-800" />
                <h3 className="text-sm font-semibold text-slate-800">Personalizzazione portale</h3></div>
              <p className="text-[11px] text-slate-500">Il portale personalizzato mostra logo, denominazione e colori al dipendente prima del login.</p>
            </div>
            <div><Label>Logo aziendale (URL o data-URI)</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://azienda.it/logo.png" data-testid="company-form-logo-url" /></div>
            <div>
              <Label>Colore principale (hex)</Label>
              <div className="flex items-center gap-2">
                <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} placeholder="es. #0284c7" data-testid="company-form-primary-color" />
                {form.primary_color && <div className="w-8 h-8 rounded border" style={{ backgroundColor: form.primary_color }} />}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Slug portale (URL)</Label>
              <div className="flex items-center gap-2">
                <Input value={form.portal_slug} onChange={(e) => setForm({ ...form, portal_slug: toSlug(e.target.value) })} placeholder="es. studio-leone" data-testid="company-form-portal-slug" />
                {form.portal_slug && (
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/login/${form.portal_slug}`); toast.success("Link copiato"); }}
                    data-testid="copy-portal-link-btn"><Copy size={12} className="mr-1" /> Copia link</Button>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <Link2 size={11} />
                <code className="px-1 py-0.5 rounded bg-slate-100">{window.location.origin}/login/{form.portal_slug || toSlug(form.name) || "azienda"}</code>
              </div>
            </div>
            <div>
              <Label>Colore secondario</Label>
              <Input value={form.custom_settings?.secondary_color || ""}
                onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, secondary_color: e.target.value } })}
                placeholder="es. #64748b"
                data-testid="company-form-secondary-color" />
            </div>
            <div>
              <Label>Titolo del portale</Label>
              <Input value={form.custom_settings?.portal_title || ""}
                onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, portal_title: e.target.value } })}
                placeholder="es. Portale dipendenti Studio Leone"
                data-testid="company-form-portal-title" />
            </div>
            <div>
              <Label>Sottotitolo del portale</Label>
              <Input value={form.custom_settings?.portal_subtitle || ""}
                onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, portal_subtitle: e.target.value } })}
                placeholder="es. Accedi al tuo cartellino mensile"
                data-testid="company-form-portal-subtitle" />
            </div>
            <div className="md:col-span-2">
              <Label>Immagine di sfondo login (URL)</Label>
              <Input value={form.custom_settings?.login_background_url || ""}
                onChange={(e) => setForm({ ...form, custom_settings: { ...form.custom_settings, login_background_url: e.target.value } })}
                placeholder="https://azienda.it/sfondo-login.jpg"
                data-testid="company-form-login-bg" />
            </div>
          </fieldset>
        </TabsContent>
      </Tabs>

      {/* Barra salva persistente per Anagrafica / Banca-ore / Portale (non per Contratti/Accessi che gestiscono da soli) */}
      {(currentTab === "anagrafica" || currentTab === "banca-ore" || currentTab === "portale") && !isExternalConsultant && (
        <div className="sticky bottom-0 mt-6 -mx-4 md:-mx-6 px-4 md:px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {isDirty ? <span className="text-amber-700 font-medium" data-testid="company-dirty-badge">Modifiche non salvate</span> : <span>Nessuna modifica in sospeso</span>}
          </div>
          <SuiteButton onClick={save} icon={Save} loading={saving} disabled={!isDirty} data-testid="company-save-btn">Salva modifiche</SuiteButton>
        </div>
      )}
    </div>
  );
}
